import { Text, Stack, Heading, List } from 'braid-design-system';
import React from 'react';

import type { ListingProduct } from 'src/generated/graphql';
import type { UsePostListingStatus } from 'src/hooks/usePostListing';

import { PaymentSuccessCta } from './PaymentSuccessCTA';
import { PaymentSuccessLayout } from './PaymentSuccessContainer';
import {
  shouldRenderPostListingFeedback,
  PostListingFeedback,
} from './PostListingFeedback';

interface BoostPaymentSuccessProps {
  variant: 'email-boost-only' | 'search-boost-only' | 'email-and-search-boost';
  baseContract?: ListingProduct;
  postListingStatus?: UsePostListingStatus;
}

export const BoostPaymentSuccess = ({
  variant,
  baseContract,
  postListingStatus,
}: BoostPaymentSuccessProps) => {
  const hasSearchBoost =
    variant === 'search-boost-only' || variant === 'email-and-search-boost';
  const hasEmailBoost =
    variant === 'email-boost-only' || variant === 'email-and-search-boost';
  const enquiriesEmailLink = `mailto:enquiries@seekbusiness.com.au?subject=${encodeURIComponent(
    getEmailSubjectFromVariant(variant),
  )}`;

  return (
    <PaymentSuccessLayout
      callToAction={<PaymentSuccessCta>Next</PaymentSuccessCta>}
    >
      <Stack space="large" data={{ testid: 'boost-payment-success' }}>
        <Heading level="3">Purchase success!</Heading>

        <Stack space="large">
          <Stack space="medium">
            <Text>We&apos;ve received your order for:</Text>

            <List space="small">
              {baseContract && <Text>{baseContract.description}</Text>}
              {hasSearchBoost && <Text>Website Search Boost</Text>}
              {hasEmailBoost && <Text>Email Boost</Text>}
            </List>
          </Stack>

          <Text>
            You will receive an email to confirm your purchase and your ad will
            automatically be boosted. No further action is required on your
            part.
          </Text>

          <Text>Get ready to accelerate your business sale.</Text>

          {/* TODO: 
            In <GenericPaymentSuccess /> the copy displayed takes into account
            whether the user needs to create an ad or not. This above <BoostPaymentSuccess />
            does not do this. This was not done as it adds complexity that I
            would rather not deal with at this time.
            E.g. displaing "Create ad" rather than "Next" in the CTA */}
          {postListingStatus &&
            shouldRenderPostListingFeedback({
              loading: postListingStatus.kind === 'loading',
              error: postListingStatus.kind === 'error',
              success: postListingStatus.kind === 'success',
            }) && (
              <PostListingFeedback
                loading={postListingStatus.kind === 'loading'}
                error={postListingStatus.kind === 'error'}
                success={postListingStatus.kind === 'success'}
              />
            )}

          <Stack space="medium">
            <Text>Having troubles?</Text>
            <Text data={{ testid: 'boost-payment-success-contact-us' }}>
              Contact us at{' '}
              <a href={enquiriesEmailLink}>enquiries@seekbusiness.com.au</a>
            </Text>
          </Stack>
        </Stack>
      </Stack>
    </PaymentSuccessLayout>
  );
};

const getEmailSubjectFromVariant = (
  variant: BoostPaymentSuccessProps['variant'],
) => {
  const template = (product: string) => `Purchase Assistance | ${product}`;
  switch (variant) {
    case 'email-boost-only':
      return template('Email Boost');
    case 'search-boost-only':
      return template('Website Search Boost');
    case 'email-and-search-boost':
      return template('Website Search Boost and Email Boost');
  }
};
